import API_BASE from './config';
import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from "html5-qrcode";
import axios from 'axios';

const Scanner = () => {
    const [status, setStatus] = useState('idle'); 
    const [message, setMessage] = useState('SYSTEM INITIALIZING...');
    const [scannedData, setScannedData] = useState(''); 
    const [participant, setParticipant] = useState(null);
    const scannerRef = useRef(null);

    useEffect(() => {
        const html5QrCode = new Html5Qrcode("reader");
        scannerRef.current = html5QrCode;
        
        // Safety check: Only start if the component is still mounted
        const timer = setTimeout(() => {
            if (document.getElementById("reader")) {
                startCamera();
            }
        }, 1000);

        return () => {
            clearTimeout(timer);
            // Enhanced Cleanup: Stop the camera immediately when switching tabs
            if (scannerRef.current) {
                if (scannerRef.current.isScanning) {
                    scannerRef.current.stop()
                        .then(() => scannerRef.current.clear())
                        .catch(err => console.error("Scanner cleanup failed", err));
                } else {
                    scannerRef.current.clear();
                }
            }
        };
    }, []);

    const stopScannerPermanently = async () => {
        if (scannerRef.current && scannerRef.current.isScanning) {
            try {
                await scannerRef.current.stop();
                await scannerRef.current.clear();
            } catch (err) { console.error(err); }
        }
    };

    const startCamera = async () => {
        // Ensure the reader element exists before starting to prevent play() interruptions
        if (!scannerRef.current || scannerRef.current.isScanning || !document.getElementById("reader")) return;
        
        setParticipant(null);
        setScannedData('');
        setStatus('scanning');
        setMessage('READY: ALIGN TICKET QR WITHIN FRAME');

        try {
            await scannerRef.current.start(
                { facingMode: "environment" },
                { fps: 20, qrbox: { width: 300, height: 300 }, aspectRatio: 1.0 },
                onScanSuccess,
                () => {} 
            );
        } catch (err) {
            // Ignore the specific interruption error in logs as we handle it via cleanup
            if (!err.includes("interrupted")) {
                setStatus('error');
                setMessage("HARDWARE ERROR: CAMERA PERMISSION DENIED");
            }
        }
    };

    const onScanSuccess = async (decodedText) => {
        // Check if we already got a valid result to prevent double-firing
        if (!decodedText) return;

        // Optional: Add a small vibrate or sound here to confirm physical read
        if (navigator.vibrate) navigator.vibrate(100);

        setScannedData(decodedText);
        verifyTicket(decodedText);

        // Stop the scanner only AFTER we have the data
        if (scannerRef.current && scannerRef.current.isScanning) {
            await scannerRef.current.stop().catch(e => console.log(e));
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !scannerRef.current) return;

        setStatus('loading');
        setMessage('DECODING SECURE IMAGE DATA...');

        try {
            if (scannerRef.current.isScanning) {
                await scannerRef.current.stop();
            }
            const decodedText = await scannerRef.current.scanFile(file, true);
            setScannedData(decodedText);
            verifyTicket(decodedText);
        } catch (err) {
            setStatus('error');
            setMessage("SCAN FAILED: NO VALID QR DETECTED");
        }
    };

    const verifyTicket = (token) => {
        setStatus('loading');
        setMessage(`VERIFYING TOKEN...`);
        
        const currentPin = localStorage.getItem('staff_pin');

        axios.post(`${API_BASE}/api/registrations/verify_ticket/`, { 
            token: token,
            pin: currentPin 
        })
            .then(res => {
                setStatus('success');
                setParticipant(res.data);
                setMessage('AUTHENTICATION SUCCESSFUL');
            })
            .catch(err => {
                setStatus('error');
                const errorMsg = err.response?.data?.message || err.response?.data?.error || "TICKET NOT FOUND";
                // Extra visual cue for "Already Checked In"
                if (errorMsg.toLowerCase().includes('already')) {
                    setStatus('error');
                    setMessage("SECURITY ALERT: TICKET ALREADY USED");
                    return;
                }
                setMessage(`DENIED: ${errorMsg.toUpperCase()}`);
            });
    };

    return (
        <div className="max-w-xl mx-auto px-4">
            <div className="bg-[#161b22]/60 backdrop-blur-2xl border border-white/[0.08] rounded-[2.5rem] overflow-hidden shadow-2xl transition-all duration-500">
                <div className="p-8 text-center border-b border-white/[0.05]">
                    <h2 className="text-2xl font-black tracking-tight text-white uppercase">Gate Command</h2>
                    <p className="text-[10px] uppercase tracking-[0.4em] text-sky-500 font-bold mt-2 italic">Infrastructure Secure</p>
                </div>

                <div className="p-6 md:p-8">
                    <div className="relative rounded-[2rem] overflow-hidden bg-black aspect-square border border-white/5 shadow-inner">
                        <div id="reader" className="w-full h-full"></div>
                        
                        {status === 'success' && participant && (
                            <div className="absolute inset-0 bg-sky-500 flex flex-col items-center justify-center text-black p-8 text-center z-20 animate-in fade-in zoom-in duration-300">
                                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center text-white text-2xl mb-6 font-black">✓</div>
                                <h3 className="text-2xl font-black tracking-tighter uppercase mb-2">Authenticated</h3>
                                <div className="space-y-1">
                                    <p className="text-xl font-bold leading-tight">{participant.name}</p>

                                    {/* Display Team Name if it exists in the API response */}
                                    {participant.team_name && (
                                        <p className="text-[10px] font-black uppercase bg-black/20 px-3 py-1 rounded-full inline-block">
                                            {participant.slot_time === "Concert Ticket" ? "Tier" : "Team"}: {participant.team_name}
                                        </p>
                                    )}
                                </div>
                                <p className="text-[10px] uppercase tracking-[0.2em] opacity-60 mt-3 font-bold">
                                    {participant.segment}
                                </p>

                                {participant.slot_time && participant.slot_time !== "N/A" && (
                                    <div className="mt-3 bg-black/20 border border-black/10 rounded-2xl px-4 py-3">
                                        <p className="text-[8px] font-black uppercase tracking-[0.3em] opacity-60 mb-1">
                                            Date & Time Slot
                                        </p>
                                        <p className="text-sm font-black leading-tight">
                                            {participant.slot_time}
                                        </p>
                                    </div>
                                )}
                                
                                <div className="mt-8 pt-6 border-t border-black/10 w-full">
                                    <button 
                                        onClick={startCamera}
                                        className="bg-black text-white w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black/90 active:scale-95 transition-all shadow-xl"
                                    >
                                        Next Scan
                                    </button>
                                </div>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="absolute inset-0 bg-red-600 flex flex-col items-center justify-center text-white p-8 text-center z-20 animate-in slide-in-from-top-4">
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-red-600 text-2xl mb-6 font-black">!</div>
                                <h3 className="text-2xl font-black tracking-tighter uppercase mb-2">Entry Denied</h3>
                                <p className="text-xs font-bold opacity-90 mb-4">{message}</p>
                                
                                <div className="bg-black/20 p-3 rounded-xl mb-8 w-full border border-white/10">
                                    <p className="text-[8px] uppercase tracking-[0.3em] opacity-60 mb-1 font-bold">Scanned Raw Data:</p>
                                    <p className="text-[10px] font-mono break-all leading-tight">
                                        {scannedData || "NO DATA CAPTURED"}
                                    </p>
                                </div>

                                <button 
                                    onClick={startCamera}
                                    className="bg-white text-red-600 w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest active:scale-95 transition-all shadow-lg"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}
                    </div>

                    {status !== 'success' && status !== 'error' && (
                        <div className="mt-8 flex flex-col sm:flex-row gap-4">
                            <label className="flex-1 bg-white text-black py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest text-center cursor-pointer active:scale-95 transition-all">
                                Upload Pass
                                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                            </label>
                            <button 
                                onClick={() => { if(!scannerRef.current?.isScanning) startCamera() }} 
                                className="flex-1 bg-white/5 border border-white/10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white active:scale-95 transition-all"
                            >
                                Reset Cam
                            </button>
                        </div>
                    )}

                    <div className="mt-8 p-4 bg-black/40 rounded-xl border border-white/5">
                        <p className="text-center text-[10px] font-bold uppercase tracking-widest leading-relaxed text-gray-500">
                            {message}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Scanner;